import {
  Avatar,
  Button,
  Dropdown,
  Image,
  TableColumnsType,
  Tag,
  Tooltip,
} from "antd";
import Header from "../../atoms/Header";
import TableUi from "../../atoms/Table";
import DashboardTemplate from "../../templates/Dashboard";
import Filters from "./Components/Filters";
import { useEffect, useRef, useState } from "react";
import { ClientsType } from "./ducks/types";
import { PopUp } from "../../atoms/PopUp";
import EmptyCard from "../../atoms/EmptyCard";
import {
  CrossIcon,
  DeleteIcon,
  NeedHelp,
  TickIcon,
} from "../../atoms/CustomIcons";
import ConfirmDelete from "../../atoms/ConfirmDelete";
import { useAppDispatch, useAppSelector } from "../../config/hooks";
import {
  getPropertiesListing,
  removePropertiesListData,
} from "./ducks/actions";
import SuccessMessage from "../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../atoms/Toasts/ErrorMessage";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { getCompanyFieldsById } from "../CompanySettings/TagsCategories/ducks/actions";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import { changePropertyStatus, deleteProperty } from "./ducks/services";
import { getInitials } from "../../config/utils";
import AccountResponsiblePopUp from "./Components/AccountResponsiblePopUp";

export default function Properties() {
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const navigate = useNavigate();
  const companyId = pathSegments[pathSegments.length - 1];
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableRecord, setTableRecord] = useState<ClientsType | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAccountResponsibleOpen, setIsAccountResponsibleOpen] =
    useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);
  const dispatch = useAppDispatch();
  const propertyListData = useAppSelector(
    (state) => state?.properties?.propertyListData
  );

  const tagItems = useAppSelector((state) => state?.tags?.tagData);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "PROPERTIES",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  const [filters, setFilters] = useState<{
    type: undefined | string;
    status: undefined | string[];
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    type: undefined,
    status: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/save-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          New deal
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/change-user.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            setIsAccountResponsibleOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          Change account responsible
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/reset-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/transfer-property/${companyId}`);
          }}
        >
          Transfer to another client
        </Button>
      ),
    },
    {
      key: "4",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/edit-property/${tableRecord?.id}/${companyId}`);
          }}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "5",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/archive-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            setArchivePopUp(true);
          }}
        >
          {tableRecord?.status === "active" ? "Archive" : "Activate"}
        </Button>
      ),
    },
    {
      key: "6",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />
          Active
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "archived",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            archiveFunction();
            if (dropdownRef.current) {
              dropdownRef?.current?.click();
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />
          Archived
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<ClientsType> = [
    {
      title: "Property name",
      dataIndex: "name",
      width: 281,
      render: (text, record) =>
        text ? `${text} ${record?.last_name ?? ""}` : "-",
      fixed: "left",
    },
    {
      title: "Category",
      dataIndex: "category_ids",
      width: 150,
      render: (text: string[]) =>
        text?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {text?.slice(0, 1).map((item: string) => {
              const tagDetails: { name: string } = tagItems?.data?.find(
                (tag: { id: string }) => tag?.id === item
              ) || { name: "" };

              return (
                <Tag
                  key={item}
                  style={{ textTransform: "capitalize" }}
                  className="white_tag"
                >
                  {tagDetails?.name}
                </Tag>
              );
            })}

            {text?.length > 1 && (
              <Tooltip
                title={text
                  ?.slice(1)
                  ?.map((itemId: string) => {
                    const tagDetails: { name: string } = tagItems?.data?.find(
                      (tag: { id: string }) => tag?.id === itemId
                    ) || { name: "" };
                    return tagDetails?.name;
                  })
                  ?.join(", ")}
              >
                <Tag
                  style={{
                    background: "#fff",
                    color: "#151718",
                    borderColor: "#EFF2F3",
                    cursor: "pointer",
                  }}
                >
                  +{text?.length - 1}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: (
        <Tooltip title="Property owning company or individual">Client</Tooltip>
      ),
      dataIndex: "client",
      width: 200,
      render: (text) => (text ? text?.name : "-"),
    },
    {
      title: "City",
      dataIndex: "city",
      width: 140,
      render: (text: string) => (text ? text : "-"),
    },
    {
      title: "State",
      dataIndex: "state",
      width: 100,
      render: (text: string) => (text ? text : "-"),
    },
    {
      title: (
        <Tooltip title="Shows if there’s an ongoing contract or a potential deal">
          Contract Status
        </Tooltip>
      ),
      dataIndex: "contract_status",
      width: 150,
      render: (text) =>
        text?.length > 0 ? <Tag className="yellow_tag"> {text} </Tag> : "-",
    },
    {
      title: (
        <Tooltip title="Number of open potential deals">
          Potential deals
        </Tooltip>
      ),
      dataIndex: "deals_count",
      width: 143,
      render: (text) => (text && text > 0 ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Number of ongoing contracts">Open Contracts</Tooltip>
      ),
      dataIndex: "open_contracts",
      width: 143,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Account or sales manager">
          Responsible for account
        </Tooltip>
      ),
      dataIndex: "responsible_manager",
      width: 200,
      render: (text) =>
        text ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            {text?.image_url ? (
              <Avatar
                src={text?.image_url}
                size={24}
                style={{ marginRight: 8 }}
              />
            ) : (
              <Avatar size={24} style={{ marginRight: 8 }}>
                {getInitials(text?.first_name, text?.last_name)}
              </Avatar>
            )}

            <div style={{ textTransform: "capitalize", display: "flex" }}>
              <span> {text?.first_name ?? ""} </span>
              <span
                style={{
                  display: "inline-block",
                  maxWidth: "16px",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  marginLeft: "3px",
                }}
              >
                {text?.last_name ?? ""}
              </span>
            </div>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: (
        <Tooltip title="Main point of contact at property">
          Main Contact
        </Tooltip>
      ),
      dataIndex: "main_contracts",
      width: 180,
      render: (text) => (text ? text : "-"),
    },
    {
      title: (
        <Tooltip title="Contact person for billing and payment-related matters">
          Billing Contact
        </Tooltip>
      ),
      dataIndex: "billing_contracts",
      width: 195,
      render: (text) => (text ? text : "-"),
    },

    {
      title: "Production managers",
      dataIndex: "billing_contracts",
      width: 195,
      render: (text: string[]) =>
        text?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {text?.slice(0, 1).map((item: string) => {
              const tagDetails: { color: string; name: string } =
                tagItems?.data?.find(
                  (tag: { id: string }) => tag?.id === item
                ) || { color: "", name: "" };

              return (
                <Tag
                  key={item}
                  style={{ textTransform: "capitalize" }}
                  className={tagDetails?.color}
                >
                  {tagDetails?.name}
                </Tag>
              );
            })}

            {text?.length > 1 && (
              <Tooltip
                title={text
                  ?.slice(1)
                  ?.map((itemId: string) => {
                    const tagDetails: { name: string } = tagItems?.data?.find(
                      (tag: { id: string }) => tag?.id === itemId
                    ) || { name: "" };
                    return tagDetails?.name;
                  })
                  ?.join(", ")}
              >
                <Tag
                  style={{
                    background: "#EFF2F3",
                    color: "#151718",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  +{text?.length - 1}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
    },

    {
      title: "Crews",
      dataIndex: "category_ids",
      width: 320,
      render: (text: string[]) =>
        text?.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {text?.slice(0, 1).map((item: string) => {
              const tagDetails: { color: string; name: string } =
                tagItems?.data?.find(
                  (tag: { id: string }) => tag?.id === item
                ) || { color: "", name: "" };

              return (
                <Tag
                  key={item}
                  style={{ textTransform: "capitalize" }}
                  className={tagDetails?.color}
                >
                  {tagDetails?.name}
                </Tag>
              );
            })}

            {text?.length > 1 && (
              <Tooltip
                title={text
                  ?.slice(1)
                  ?.map((itemId: string) => {
                    const tagDetails: { name: string } = tagItems?.data?.find(
                      (tag: { id: string }) => tag?.id === itemId
                    ) || { name: "" };
                    return tagDetails?.name;
                  })
                  ?.join(", ")}
              >
                <Tag
                  style={{
                    background: "#EFF2F3",
                    color: "#151718",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  +{text?.length - 1}
                </Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: (
        <Tooltip title="Total amount of all active contracts">
          Active contracts sum, $
        </Tooltip>
      ),
      dataIndex: "active_contracts_sum",
      width: 194,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Sum of all unpaid invoices">
          Accounts receivable, $
        </Tooltip>
      ),
      dataIndex: "accounts_receivable",
      width: 194,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Earned revenue, year-to-date">
          Earned revenue (YTD), $
        </Tooltip>
      ),
      dataIndex: "earned_revenue_ytd",
      width: 205,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: <Tooltip title="Gross margin, year-to-date">GM (YTD), %</Tooltip>,
      dataIndex: "gross_margin",
      width: 130,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Gross profit, year-to-date">
          Gross profit (YTD), $
        </Tooltip>
      ),
      dataIndex: "gross_profit",
      width: 180,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: <Tooltip title="Win ratio, year-to-date">Win ratio (YTD)</Tooltip>,
      dataIndex: "wind_ratio",
      width: 150,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Earned revenue, rolling 12 months">
          Earned revenue (R12M), $
        </Tooltip>
      ),
      dataIndex: "earned_revenue_r12m",
      width: 205,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Gross margin, rolling 12 months">GM (R12M), %</Tooltip>
      ),
      dataIndex: "gross_margin_r12m",
      width: 130,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Gross profit, rolling 12 months">
          Gross profit (R12M), $
        </Tooltip>
      ),
      dataIndex: "gross_profit_r12m",
      width: 180,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: (
        <Tooltip title="Win ratio, rolling 12 months">Win ratio (R12M)</Tooltip>
      ),
      dataIndex: "wind_ratio_r12m",
      width: 150,
      render: (text) => (text ? text : "-"),
      align: "right",
    },
    {
      title: "Property status",
      dataIndex: "status",
      width: 140,
      render: (text, record) => (
        <Dropdown
          menu={{
            items: statusItems,
            selectable: true,
            selectedKeys: [text],
          }}
          trigger={["click"]}
          overlayClassName="status_dropdown_overlay"
        >
          <Button
            onClick={(e) => {
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className={`status_dropdown ${text}_status`}
            ref={dropdownRef}
            style={{ textTransform: "capitalize" }}
          >
            {text}{" "}
            <div className="icon_arrow">
              <CaretDownOutlined />
            </div>
          </Button>
        </Dropdown>
      ),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const fetchUserListing = (status: string) => {
    const payload = {
      company_id: companyId,
      offset: 0,
      limit: 10,
      status: [status ?? "active"],
    };
    dispatch(getPropertiesListing(payload));
  };

  const deleteFunction = () => {
    setLoading(true);
    deleteProperty(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchUserListing(tableRecord?.status ?? "active");
        SuccessMessage("Property deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const accountResponsible = {
    visibility: isAccountResponsibleOpen,
    content: (
      <AccountResponsiblePopUp
        onClose={() => setIsAccountResponsibleOpen(false)}
        title="Change responsible for the account"
      />
    ),
    width: 480,
    onCancel: () => setIsAccountResponsibleOpen(false),
  };

  const deleteClientPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          selectedRowKeys?.length > 0 ? (
            <>
              Are you sure you want to delete selected{" "}
              {selectedRowKeys.length === 1 ? "property" : "properties"}?
              <br />
              This action cannot be undone.
            </>
          ) : (
            <>
              Are you sure you want to delete <b>{tableRecord?.name}</b>?<br />
              <br />
              This action cannot be undone.
            </>
          )
        }
        btnText={
          selectedRowKeys?.length > 0
            ? `Delete ${selectedRowKeys?.length} ${
                selectedRowKeys.length === 1 ? "property" : "properties"
              }`
            : "Delete property"
        }
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const archiveFunction = () => {
    setLoading(true);
    const payload = {
      status: tableRecord?.status === "active" ? "archived" : "active",
    };
    changePropertyStatus(tableRecord?.id as string, payload)
      .then(() => {
        setLoading(false);
        setArchivePopUp(false);
        fetchUserListing(tableRecord?.status ?? "active");
        SuccessMessage(
          `Property ${
            tableRecord?.status === "active" ? "archived" : "activated"
          }.`
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmArchivedPopUp = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading={tableRecord?.status === "active" ? "Archive" : "Activate"}
        paragraph={
          <>
            Are you sure you want to{" "}
            {tableRecord?.status === "active" ? "archive" : "activate"}{" "}
            <b>{tableRecord?.name}</b>? <br /> <br />
            {tableRecord?.status === "active" && (
              <>This property won’t be available for use.</>
            )}
          </>
        }
        btnText={`${
          tableRecord?.status === "active" ? "Archive" : "Activate"
        } property`}
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  useEffect(() => {
    dispatch(removePropertiesListData());
  }, [dispatch]);

  useEffect(() => {
    if (companyId) {
      const payload = {
        status: filters?.status ?? ["active"],
        offset: 0,
        limit: 10,
        company_id: companyId,
        type: filters?.type ?? undefined,
        search_text: filters?.search_text ?? undefined,
      };
      dispatch(getPropertiesListing(payload));
    }
  }, [dispatch, filters, pagination, limit, companyId]);

  const onClickRow = (record: ClientsType) => {
    return {
      onClick: () => {
        navigate(`/properties/profile/${record.id}/${companyId}`);
      },
    };
  };

  return (
    <DashboardTemplate loading={loading || propertyListData?.loader}>
      <Header
        title="Properties"
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />

      <TableUi<ClientsType>
        columns={columns}
        data={{
          count: propertyListData?.count,
          data: propertyListData?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="Add new property"
        createButtonLink={`/add-property/${companyId}`}
        onClickRow={onClickRow}
      />
      {!propertyListData?.loader && propertyListData?.data?.length === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no archived properties"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} properties`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have an archived properties here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "property" : "properties"}`}{" "}
            selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteClientPopUp} />
      <PopUp {...confirmArchivedPopUp} />
      <PopUp {...accountResponsible} />
    </DashboardTemplate>
  );
}
